import React, { useState, useEffect } from 'react';

import FormFields from 'pages/components/FormFields';
import { useTranslation } from 'react-i18next';
import { Box, Chip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';

type FilterType =
  | 'bool'
  | 'date'
  | 'string'
  | 'number'
  | Array<{
      label: string;
      value: string;
    }>;

export type FilterConfig = Record<string, FilterType>;

type SearchFilterProps = {
  filterConfig: FilterConfig;
  filterValues: Record<string, string>;
  updateFilters: Function;
};

interface SelectedFieldsValues {
  [key: string]: any;
}

export default function SearchFilter({
  filterConfig,
  filterValues,
  updateFilters,
}: SearchFilterProps) {
  const { t } = useTranslation();

  const [selectedField, setSelectedField] = useState(
    Object.keys(filterConfig)[0],
  );
  const [selectedFieldsValues, setSelectedFieldsValues] =
    useState<SelectedFieldsValues>(filterValues);

  let filterValuesToDisplay = filterValues;

  function handleSubmit(e: any) {
    e.preventDefault();
    filterValuesToDisplay = selectedFieldsValues;
    updateFilters(selectedFieldsValues);
  }

  function handleFieldChange(e: any) {
    const { value } = e.target;
    if (value) {
      setSelectedField(value);
    }
  }

  function updateSelectedFieldValue(name: string, value: any) {
    setSelectedFieldsValues({
      ...selectedFieldsValues,
      [name]: value,
    });
  }

  useEffect(() => {
    // make search request on each filter change
    updateFilters(selectedFieldsValues);
  }, [selectedFieldsValues]);

  function removeSelectedFieldValue(name: string) {
    if (name in selectedFieldsValues) {
      const selected = { ...selectedFieldsValues };
      delete selected[name];
      filterValuesToDisplay = selected;
      setSelectedFieldsValues(selected);
      updateFilters(selected);
    }
  }

  function handleResetForm(e: any) {
    e.preventDefault();
    setSelectedFieldsValues({});
    updateFilters({});
  }

  return (
    <Box border={'1px solid lightgray'} padding={2}>
      <form onSubmit={handleSubmit}>
        <Box
          display={'flex'}
          height={'50px'}
          gap={5}
          alignItems={'center'}
          justifyContent={'center'}
        >
          <FormFields.Select
            name="field"
            variant="standard"
            value={selectedField}
            onChange={handleFieldChange}
            options={Object.keys(filterConfig).map((filter) => ({
              label: t(`FORM.FIELD.${filter}`),
              value: filter,
            }))}
          />
          <Box>:</Box>
          <TypedInput
            field={selectedField}
            updateField={updateSelectedFieldValue}
            value={selectedFieldsValues[selectedField] ?? ''}
            filterType={filterConfig[selectedField]}
          />

          <Box display={'flex'} flexDirection={'column'} gap={1}>
            <FormFields.Button
              type="submit"
              color="info"
              size="small"
              label={t('FORM.SUBMIT.filter')}
            />
          </Box>
        </Box>
      </form>

      {Object.keys(filterValuesToDisplay).length > 0 && (
        <Box display={'flex'} width={'100%'}>
          <Box
            display={'flex'}
            paddingTop={2}
            flexGrow={1}
            gap={3}
            alignItems={'center'}
            justifyContent={'center'}
          >
            {Object.keys(filterValuesToDisplay).map(
              (field: string, i: number) => (
                <Chip
                  key={i}
                  size="small"
                  onDelete={() => {
                    removeSelectedFieldValue(field);
                  }}
                  label={
                    <>
                      <span style={{ fontWeight: 'bold' }}>
                        {t(`FORM.FIELD.${field}`)}:
                      </span>{' '}
                      {formatSelectedValue(
                        filterValuesToDisplay[field],
                        filterConfig[field],
                      )}
                    </>
                  }
                />
              ),
            )}
          </Box>
          <Box alignSelf={'end'} justifySelf={'end'} width={'fit-content'}>
            <FormFields.Button
              type="submit"
              size="small"
              label={t('FORM.SUBMIT.reset')}
              color="error"
              onClick={handleResetForm}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

function formatSelectedValue(value: any, filterType: FilterType) {
  if (filterType === 'bool') {
    const iconStyle = {
      marginBottom: -2,
      height: '15px',
    };

    return value ? (
      <CheckIcon style={iconStyle} color="success" />
    ) : (
      <ClearIcon style={iconStyle} color="error" />
    );
  } else if (Array.isArray(filterType)) {
    const selectedOption = filterType.find((option) => option.value === value);
    if (selectedOption) {
      return selectedOption.label;
    }
    return value;
  } else {
    return value;
  }
}

type TypedInputProps = {
  filterType: FilterType;
  field: string;
  value: any;
  updateField: Function;
};

function TypedInput({
  filterType,
  field,
  value,
  updateField,
}: TypedInputProps) {
  function handleFieldChange(e: any) {
    const { name, value } = e.target;
    updateField(name, value);
  }

  if (filterType === 'string') {
    return (
      <FormFields.TextInput
        variant="standard"
        value={value}
        name={field}
        onChange={handleFieldChange}
      />
    );
  } else if (filterType === 'bool') {
    return (
      <FormFields.CheckBox
        variant="standard"
        value={value}
        name={field}
        onChange={handleFieldChange}
      />
    );
  } else if (filterType === 'number') {
    return (
      <FormFields.TextInput
        variant="standard"
        value={value}
        type="number"
        name={field}
        onChange={handleFieldChange}
      />
    );
  } else if (filterType === 'date') {
    return (
      <FormFields.DatePicker
        value={value}
        variant="standard"
        name={field}
        onChange={handleFieldChange}
      />
    );
  } else if (Array.isArray(filterType)) {
    // if (!filter.options) {
    if (filterType.length === 0) {
      return <></>;
    }
    return (
      <FormFields.Select
        onChange={handleFieldChange}
        variant="standard"
        name={field}
        value={value}
        options={filterType}
      />
    );
  } else {
    return <></>;
  }
}
